import React, { useState } from 'react';

import { Autocomplete, CircularProgress, Stack, TextField } from '@mui/material';

import { useSearchRateCodes } from '~/hooks/apis/cruises/useSearchRateCodes';

interface Props {
  value: Array<string>;
  onChange: (codes: Array<string>) => void;
}

function RateCodeSelect({ value, onChange }: Props) {
  const [inputValue, setInputValue] = useState('');
  const searchQuery = useSearchRateCodes(inputValue);
  const options = Array.from(new Set((searchQuery.data?.result ?? []).map((rate) => rate.code.trim())));
  const loading = !!inputValue.trim() && (searchQuery.isFetching || searchQuery.isPending);

  return (
    <Stack mb={3}>
      <Autocomplete
        multiple
        filterSelectedOptions
        value={value}
        inputValue={inputValue}
        options={options}
        filterOptions={(items) => items}
        loading={loading}
        onChange={(_, codes) => onChange(codes)}
        onInputChange={(_, text) => setInputValue(text)}
        noOptionsText={inputValue.trim() ? 'No rate codes found.' : 'Enter a complete rate code to search.'}
        renderOption={({ key, ...props }, code) => (
          <li {...props} key={key}>
            {code} —{' '}
            {Array.from(
              new Set(
                searchQuery.data?.result.filter((rate) => rate.code.trim() === code).map((rate) => rate.vendor.name),
              ),
            ).join(', ')}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Rate codes"
            placeholder="Enter a complete rate code"
            error={searchQuery.isError}
            helperText={searchQuery.isError ? 'Unable to search rate codes. Please try again.' : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} aria-label="Searching rate codes" />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Stack>
  );
}

export default RateCodeSelect;
